import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Handshake, Users, ShieldCheck, Sparkles, MapPin, Search, X, Filter,
} from 'lucide-react';
import { api, municipiosApi } from '../services/api';
import Breadcrumbs from '../components/Breadcrumbs';
import useTenantGeoRedirect from '../hooks/useTenantGeoRedirect';
import { resolveBackendImage } from '../utils/brandLogo';

const TIPO_BADGE = {
  feira: 'bg-amber-100 text-amber-800',
  cooperativa: 'bg-green-100 text-green-800',
  associacao: 'bg-blue-100 text-blue-800',
  grupo: 'bg-purple-100 text-purple-800',
  rede: 'bg-pink-100 text-pink-800',
};
const TIPO_LABEL = {
  feira: 'Feira', cooperativa: 'Cooperativa', associacao: 'Associação',
  grupo: 'Grupo', rede: 'Rede',
};

export default function ColetivosPublico() {
  const { municipioSlug } = useParams();
  useTenantGeoRedirect({ routeName: 'coletivos', currentSlug: municipioSlug });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ tipo: {} });
  const [loading, setLoading] = useState(true);
  const [municipio, setMunicipio] = useState(null);

  const [filterTipo, setFilterTipo] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!municipioSlug) { setMunicipio(null); return; }
    municipiosApi.get(municipioSlug)
      .then((m) => setMunicipio(m || null))
      .catch(() => setMunicipio(null));
  }, [municipioSlug]);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (municipioSlug) params.municipio_slug = municipioSlug;
    if (filterTipo !== 'all') params.tipo = filterTipo;
    api.get('/v2/coletivos', { params })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
        setFacets(r.data?.facets || { tipo: {} });
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [municipioSlug, filterTipo]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((c) =>
      (c.nome || '').toLowerCase().includes(q)
      || (c.descricao || '').toLowerCase().includes(q)
      || (c.categorias || []).some((cat) => cat.toLowerCase().includes(q))
    );
  }, [items, search]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50/50 to-white" data-testid="coletivos-publico-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-purple-600 to-pink-600">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px, 60px 60px',
        }} />
        <div className="relative max-w-6xl mx-auto px-4 py-12 md:py-16">
          <Breadcrumbs items={[{ label: 'Coletivos' }]} municipio={municipio} className="mb-4" />
          <div className="text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm mb-4">
              <Handshake className="w-4 h-4" />
              <span className="text-xs uppercase font-semibold tracking-wider">Comunidade de Coletivos</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight">
              Coletivos em{' '}
              <span className="text-amber-300">{municipio?.nome || 'andamento'}</span>
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-3 max-w-2xl">
              Feiras, cooperativas, associações e grupos que pulsam na vida da cidade.
              Conheça quem faz parte, agendas e como participar.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 text-sm items-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm">
                <Sparkles className="w-4 h-4" />
                <span><strong>{total}</strong> coletivo(s)</span>
              </div>
              <Link
                to="/coletivos/cadastrar"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-purple-700 font-semibold hover:bg-purple-50 transition-colors"
                data-testid="btn-cadastrar-coletivo"
              >
                <Handshake className="w-4 h-4" />
                Cadastrar coletivo
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="coletivos-search"
              placeholder="Buscar por nome, descrição ou categoria…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm focus:border-purple-400 focus:outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterTipo('all')}
              data-testid="coletivos-tipo-chip-all"
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filterTipo === 'all'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
              }`}
            >
              Todos <span className="opacity-70">({total})</span>
            </button>
            {Object.entries(facets.tipo || {}).map(([tipo, count]) => (
              <button
                key={tipo}
                onClick={() => setFilterTipo(tipo)}
                data-testid={`coletivos-tipo-chip-${tipo}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  filterTipo === tipo
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                }`}
              >
                {TIPO_LABEL[tipo] || tipo} <span className="opacity-70">({count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Carregando coletivos…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <Filter className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">
              {search ? 'Nenhum coletivo encontrado' : 'Nenhum coletivo por aqui'}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {search ? 'Tente outra palavra-chave.' : 'Volte em breve — novos coletivos são criados regularmente.'}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coletivos-grid">
            {filtered.map((c) => (
              <ColetivoCard key={c.id} c={c} basePath={municipioSlug ? `/${municipioSlug}` : ''} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}

function ColetivoCard({ c, basePath }) {
  return (
    <Link
      to={`${basePath}/coletivos/${c.slug}`}
      data-testid={`coletivo-card-${c.id}`}
      className={`group relative block rounded-xl overflow-hidden bg-white border ${c.featured ? 'border-amber-300 shadow-md' : 'border-gray-100'} hover:border-purple-300 hover:shadow-lg transition`}
    >
      <div className="relative h-36 overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200">
        {c.foto_capa && (
          <img src={resolveBackendImage(c.foto_capa)} alt={c.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <span className={`absolute top-2 left-2 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TIPO_BADGE[c.tipo] || TIPO_BADGE.grupo}`}>
          {TIPO_LABEL[c.tipo] || c.tipo}
        </span>
        {c.verificado && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
            <ShieldCheck className="w-3 h-3" /> Verificado
          </span>
        )}
        {c.featured && (
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold uppercase">
            <Sparkles className="w-3 h-3" /> Destaque
          </span>
        )}
      </div>
      <div className="px-4 pt-3 pb-4 relative">
        {c.foto_perfil && (
          <img src={resolveBackendImage(c.foto_perfil)} alt="" className="w-14 h-14 rounded-full object-cover absolute -top-7 right-4 border-4 border-white shadow-md" />
        )}
        <h3 className="font-bold text-lg leading-tight pr-12 group-hover:text-purple-700 transition line-clamp-1">
          {c.nome}
        </h3>
        {c.descricao && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{c.descricao}</p>}
        {(c.categorias || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {c.categorias.slice(0, 3).map((cat) => (
              <span key={cat} className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded-full">{cat}</span>
            ))}
            {c.categorias.length > 3 && (
              <span className="text-[10px] text-gray-500">+{c.categorias.length - 3}</span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 text-xs">
          <span className="inline-flex items-center gap-1 text-gray-600">
            <Users className="w-3 h-3" />
            <strong>{c.total_membros_aprovados || 0}</strong> membros
          </span>
          {c.localizacao?.bairro && (
            <span className="inline-flex items-center gap-1 text-gray-500">
              <MapPin className="w-3 h-3" /> {c.localizacao.bairro}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
